import type { QueryKey } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import { normalizeId } from 'utils/ids';

function normalizeKeySegment(id: unknown): string | undefined {
  return typeof id === 'string' ? normalizeId(id) : undefined;
}

/**
 * Query keys for the node details panel.
 *
 * `dataNodeId` sits before `proposalId`/`templateNodeId` so every cached variant of one node
 * (live plus each open proposal) can be matched with the `node` prefix.
 */
export const nodeDetailQueryKeys = {
  all: () => [QUERY_KEYS.NODE_DETAIL] as const,

  node: (projectId: string, dataNodeId: string) => [QUERY_KEYS.NODE_DETAIL, projectId, dataNodeId] as const,

  detail: (projectId: string, dataNodeId: string, proposalId: string | undefined, templateNodeId: string) =>
    [QUERY_KEYS.NODE_DETAIL, projectId, dataNodeId, proposalId, templateNodeId] as const,

  /** Reads the proposal variant back out of a key built by `detail`. */
  proposalIdOf: (queryKey: QueryKey): string | undefined => (typeof queryKey[3] === 'string' ? queryKey[3] : undefined),

  /**
   * Build a case-insensitive `node`-prefix predicate. Normalizes the target ids once so a single
   * event can be matched against every cached key without re-normalizing them per candidate.
   */
  nodeMatcher: (projectId: string, dataNodeId: string): ((queryKey: QueryKey) => boolean) => {
    const project = normalizeId(projectId);
    const node = normalizeId(dataNodeId);
    return (queryKey: QueryKey): boolean =>
      queryKey[0] === QUERY_KEYS.NODE_DETAIL &&
      normalizeKeySegment(queryKey[1]) === project &&
      normalizeKeySegment(queryKey[2]) === node;
  }
};
